import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { GRID_OPTION } from '@/tools/exportExcel/exportExcelPanel.constants';
import {
  buildExportExcelPanelTimeRange,
  generateDefaultGridSize,
  getExportExcelPanelSamplesMode,
} from '@/tools/exportExcel/exportExcelPanel.utilities';
import { SAMPLES_TABLE_MODE } from '@/tools/exportOData/odata.constants';
import {
  getNextToolParameterUnsetValue,
  getNextToolParameterValue,
  getRequiredToolParameterDefinition,
} from '@/tools/tool.utilities';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type { Moment } from 'moment';
import type { Timezone } from '@/datetime/timezone.service';
import type { SamplesTableMode } from '@/tools/exportExcel/exportExcelPanel.types';
import type { ParameterDefinitions, ToolItem } from '@/tools/tool.types';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';

type GridOptionValue = (typeof GRID_OPTION)[keyof typeof GRID_OPTION];

export interface ExportExcelPanelTimeRange {
  start: number;
  end: number;
}

export interface ExportExcelPanelState {
  /** Name of the exported file, defaults to the workbook/worksheet name. */
  name: string | undefined;
  /** Whether to include a statistics sheet in the export. */
  exportStatistics: boolean;
  /** Whether to include a samples sheet in the export. */
  exportSamples: boolean;
  /** Whether to include a capsules sheet in the export. */
  exportCapsules: boolean;
  /** Signals selected for export, populated from the trend selection when the tool is opened. */
  exportSignals: ToolItem[] | undefined;
  /** Conditions selected for export, populated from the trend selection when the tool is opened. */
  exportConditions: ToolItem[] | undefined;
  /** Time range for the export, in epoch milliseconds. */
  timeRange: ExportExcelPanelTimeRange;
  /** Time zone the export is formatted in; falls back to the worksheet time zone when unset. */
  exportTimeZone: Timezone | undefined;
  /** Which grid-sizing strategy to use for the exported samples. */
  gridOption: GridOptionValue;
  /** Grid size used when gridOption is CUSTOM. */
  gridSize: ValueWithUnitsItem;
  /** Origin used for gridding, in epoch milliseconds. */
  gridOrigin: number;
  /** Whether gridOrigin should be applied when gridOption is CUSTOM. */
  gridOriginEnabled: boolean;
  /** Whether the samples table should be rendered in chain-view mode. */
  chainView: boolean;
  /** Whether the samples table should be rendered in capsule-time mode. */
  capsuleTime: boolean;
}

export interface ExportExcelPanelActions {
  setSearchName: (name: string) => void;
  setExportStatistics: (exportStatistics: boolean) => void;
  setExportSamples: (exportSamples: boolean) => void;
  setExportCapsules: (exportCapsules: boolean) => void;
  /** Sets the time range start value. Ensures start is not after end. */
  setTimeRangeStart: (start: Moment | Date) => void;
  /** Sets the time range end value. Ensures end is not before start. */
  setTimeRangeEnd: (end: Moment | Date) => void;
  /** Shifts the time range start by the given duration in milliseconds, keeping end fixed. */
  setDuration: (durationMs: number) => void;
  setExportTimeZone: (exportTimeZone: Timezone) => void;
  setGridOption: (gridOption: GridOptionValue) => void;
  setGridSize: (gridSize: ValueWithUnitsItem) => void;
  setGridOrigin: (gridOrigin: Moment | Date) => void;
  setGridOriginEnabled: (gridOriginEnabled: boolean) => void;
  /** Sets chainView/capsuleTime together from the selected samples table mode. */
  setSamplesMode: (samplesMode: SamplesTableMode) => void;
  setExportSignalItem: (item: ToolItem | null | undefined) => void;
  unsetExportSignalItem: (item: ToolItem | null | undefined) => void;
  setExportConditionItem: (item: ToolItem | null | undefined) => void;
  unsetExportConditionItem: (item: ToolItem | null | undefined) => void;
  reset: (overrides?: Partial<ExportExcelPanelState>) => void;
}

export type ExportExcelPanelDehydratedState = ExportExcelPanelState;

const exportItemParameterDefinitions: ParameterDefinitions<'exportSignals' | 'exportConditions'> = {
  exportSignals: { predicate: () => true, multiple: true },
  exportConditions: { predicate: () => true, multiple: true },
};

const initialState: ExportExcelPanelState = Object.freeze({
  name: undefined,
  exportStatistics: true,
  exportSamples: true,
  exportCapsules: true,
  exportSignals: undefined,
  exportConditions: undefined,
  timeRange: { start: 0, end: 0 },
  exportTimeZone: undefined,
  gridOption: GRID_OPTION.CUSTOM,
  gridSize: generateDefaultGridSize(0),
  gridOrigin: 0,
  gridOriginEnabled: false,
  chainView: false,
  capsuleTime: false,
});

export const dehydrateExportExcelPanelState = (state: ExportExcelPanelState): ExportExcelPanelDehydratedState => state;

const exportExcelPanelActionsFactory: ActionsFactory<ExportExcelPanelState, ExportExcelPanelActions> = (
  set,
  _get,
  resetState,
) => ({
  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  setExportStatistics: (exportStatistics) =>
    set((draft) => {
      draft.exportStatistics = exportStatistics;
    }),

  setExportSamples: (exportSamples) =>
    set((draft) => {
      draft.exportSamples = exportSamples;
    }),

  setExportCapsules: (exportCapsules) =>
    set((draft) => {
      draft.exportCapsules = exportCapsules;
    }),

  setTimeRangeStart: (start) =>
    set((draft) => {
      const newStart = start.valueOf();
      draft.timeRange.start = newStart;
      if (newStart > draft.timeRange.end) {
        draft.timeRange.end = newStart;
      }
    }),

  setTimeRangeEnd: (end) =>
    set((draft) => {
      const newEnd = end.valueOf();
      draft.timeRange.end = newEnd;
      if (newEnd < draft.timeRange.start) {
        draft.timeRange.start = newEnd;
      }
    }),

  setDuration: (durationMs) =>
    set((draft) => {
      draft.timeRange.start = draft.timeRange.end - durationMs;
    }),

  setExportTimeZone: (exportTimeZone) =>
    set((draft) => {
      draft.exportTimeZone = exportTimeZone;
    }),

  setGridOption: (gridOption) =>
    set((draft) => {
      draft.gridOption = gridOption;
    }),

  setGridSize: (gridSize) =>
    set((draft) => {
      draft.gridSize = gridSize;
    }),

  setGridOrigin: (gridOrigin) =>
    set((draft) => {
      draft.gridOrigin = gridOrigin.valueOf();
    }),

  setGridOriginEnabled: (gridOriginEnabled) =>
    set((draft) => {
      draft.gridOriginEnabled = gridOriginEnabled;
    }),

  setSamplesMode: (samplesMode) =>
    set((draft) => {
      draft.chainView = samplesMode === SAMPLES_TABLE_MODE.CHAIN;
      draft.capsuleTime = samplesMode === SAMPLES_TABLE_MODE.CAPSULE;
    }),

  setExportSignalItem: (item) =>
    set((draft) => {
      const definition = getRequiredToolParameterDefinition(exportItemParameterDefinitions, 'exportSignals');
      const nextValue = getNextToolParameterValue({ definition, currentValue: draft.exportSignals, item });
      if (nextValue.changed) {
        draft.exportSignals = nextValue.value as ToolItem[];
      }
    }),

  unsetExportSignalItem: (item) =>
    set((draft) => {
      const definition = getRequiredToolParameterDefinition(exportItemParameterDefinitions, 'exportSignals');
      const nextValue = getNextToolParameterUnsetValue({ definition, currentValue: draft.exportSignals, item });
      if (nextValue.changed) {
        draft.exportSignals = nextValue.value as ToolItem[];
      }
    }),

  setExportConditionItem: (item) =>
    set((draft) => {
      const definition = getRequiredToolParameterDefinition(exportItemParameterDefinitions, 'exportConditions');
      const nextValue = getNextToolParameterValue({ definition, currentValue: draft.exportConditions, item });
      if (nextValue.changed) {
        draft.exportConditions = nextValue.value as ToolItem[];
      }
    }),

  unsetExportConditionItem: (item) =>
    set((draft) => {
      const definition = getRequiredToolParameterDefinition(exportItemParameterDefinitions, 'exportConditions');
      const nextValue = getNextToolParameterUnsetValue({ definition, currentValue: draft.exportConditions, item });
      if (nextValue.changed) {
        draft.exportConditions = nextValue.value as ToolItem[];
      }
    }),

  reset: (overrides) => resetState(overrides),
});

const STORE_NAME = 'ExportExcelPanel';

const [ExportExcelPanelStoreProvider, useExportExcelPanelStoreInstance, useExportExcelPanelSelector] = createStore(
  STORE_NAME,
  initialState,
  exportExcelPanelActionsFactory,
);

export { ExportExcelPanelStoreProvider };

export const useExportExcelPanelName = () => useExportExcelPanelSelector((state) => state.name);
export const useExportExcelPanelExportStatistics = () => useExportExcelPanelSelector((state) => state.exportStatistics);
export const useExportExcelPanelExportSamples = () => useExportExcelPanelSelector((state) => state.exportSamples);
export const useExportExcelPanelExportCapsules = () => useExportExcelPanelSelector((state) => state.exportCapsules);
export const useExportExcelPanelExportSignals = () => useExportExcelPanelSelector((state) => state.exportSignals);
export const useExportExcelPanelExportConditions = () => useExportExcelPanelSelector((state) => state.exportConditions);
export const useExportExcelPanelExportTimeZone = () => useExportExcelPanelSelector((state) => state.exportTimeZone);
export const useExportExcelPanelGridOption = () => useExportExcelPanelSelector((state) => state.gridOption);
export const useExportExcelPanelGridSize = () => useExportExcelPanelSelector((state) => state.gridSize);
export const useExportExcelPanelGridOrigin = () => useExportExcelPanelSelector((state) => state.gridOrigin);
export const useExportExcelPanelGridOriginEnabled = () =>
  useExportExcelPanelSelector((state) => state.gridOriginEnabled);
export const useExportExcelPanelChainView = () => useExportExcelPanelSelector((state) => state.chainView);
export const useExportExcelPanelCapsuleTime = () => useExportExcelPanelSelector((state) => state.capsuleTime);

const selectTimeRange = createSelector(
  (state: ExportExcelPanelState) => state.timeRange,
  buildExportExcelPanelTimeRange,
);
/** The export time range, converted from raw epoch milliseconds into a moment-based range. */
export const useExportExcelPanelTimeRange = () => useExportExcelPanelSelector(selectTimeRange);

const selectSamplesMode = createSelector(
  (state: ExportExcelPanelState) => state.chainView,
  (state: ExportExcelPanelState) => state.capsuleTime,
  getExportExcelPanelSamplesMode,
);
export const useExportExcelPanelSamplesMode = () => useExportExcelPanelSelector(selectSamplesMode);

export const useExportExcelPanelActions = () => useExportExcelPanelStoreInstance().actions;

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores.
 * DO NOT use in new code.
 */
export function legacyCreateExportExcelPanelStore() {
  return createStoreInstance<ExportExcelPanelState, ExportExcelPanelActions>(
    initialState,
    exportExcelPanelActionsFactory,
    {
      storeName: STORE_NAME,
    },
  );
}

export const usePersistExportExcelPanelStore = () => {
  const register = useRegisterPersistedStore();
  const store = useExportExcelPanelStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqExportExcelPanelStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateExportExcelPanelState,
    });
  }, [register, store]);
};
